import { css } from '@inter-ikea-kompis/web-component';
import CarouselLayoutEnum from '../../enums/CarouselLayoutEnum.js';
export default class CarouselStyle {
    /**
     * Returns styles.
     *
     * @param options Options.
     * @param options.container Container.
     * @param options.container.clientWidth Client width.
     * @param options.container.scrollLeft Scroll left.
     * @param options.container.scrollWidth Scroll width.
     * @param options.itemCount = Item count.
     * @param options.fitItems "true" to fit items.
     * @param options.hasFocus "true" if focused.
     * @param options.theme Theme.
     * @param options.scrollBarHeight Scrollbar height.
     * @param options.isScrollbarHovered "true" if scrollbar is hovered.
     * @param options.showScrollbar "true" to show scrollbar.
     * @param options.paddingBottom "true" to apply padding under the carousel items.
     * @param options.direction Direction.
     * @returns Styles.
     */
    static getStyle(options) {
        const { color, border, motion, spacing } = options.theme;
        const scrollBarHeight = options.showScrollbar ? options.scrollBarHeight : 0;
        const scrollbarTrackBackground = options.showScrollbar
            ? `linear-gradient(
			to bottom,
			transparent calc(50% - ${1 / 16}rem),
			${color.border.light} calc(50% - ${1 / 16}rem) calc(50% + ${1 / 16}rem),
			transparent calc(50% + ${1 / 16}rem)
		)`
            : 'transparent';
        const scrollbarThumbBackground = options.showScrollbar
            ? `linear-gradient(
			to bottom,
			transparent calc(50% - ${1 / 16}rem),
			${color.neutral.grey900} calc(50% - ${1 / 16}rem) calc(50% + ${1 / 16}rem),
			transparent calc(50% + ${1 / 16}rem)
		)`
            : 'transparent';
        return css `
			:host {
				position: relative;
				display: block;
			}

			.slideContainerWrapper {
				overflow: hidden;
				height: 100%;
			}

			.slideContainer {
				display: ${options.fitItems ? 'grid' : 'flex'};
				grid-template-columns: ${Array(options.itemCount)
            .fill(null)
            .map(() => '100%')
            .join(' ')};
				overflow-x: auto;
				grid-column-gap: 0.08rem;
				flex-grow: 1;
				height: 100%;
				padding-bottom: ${options.paddingBottom ? spacing.space200 / 16 : 0}rem;

				/*Firefox*/
				scrollbar-color: ${color.neutral.grey900} transparent;
				scrollbar-width: ${options.showScrollbar ? 'thin' : 'none'};
			}

			${this.getFirefoxScrollbar(options)}

			.slideContainer::-webkit-scrollbar {
				/* The browser adds some inner padding with the size of 2px for some reason */
				height: ${scrollBarHeight / 16}rem;
			}
			.slideContainer::-webkit-scrollbar-track {
				background: ${scrollbarTrackBackground};
				margin-left: ${options.layout === CarouselLayoutEnum.theatre ? spacing.space50 / 16 : 0}rem;
				margin-right: ${options.layout === CarouselLayoutEnum.theatre
            ? spacing.space50 / 16
            : 0}rem;
			}
			.slideContainer::-webkit-scrollbar-thumb {
				border-radius: ${border.radius.radius64 / 16}rem;
				background: ${scrollbarThumbBackground};
			}
			.slideContainer::-webkit-scrollbar-thumb:active {
				background: ${color.neutral.grey900};
			}
			.slideContainer:focus {
				outline: none;
			}
			:host(:hover) kompis-carousel-controls {
				visibility: visible;
				opacity: 1;
			}
			.skipButton {
				position: absolute;
				z-index: 1;
				opacity: ${options.hasFocus ? 1 : 0};
				top: ${spacing.space150 / 16}rem;
				transition-property: opacity;
				transition-duration: ${motion.duration.medium};
				transition-timing-function: ${motion.ease.easy};
				${this.getRTLStyling(options)};
			}
			kompis-carousel-controls {
				opacity: ${options.layout === CarouselLayoutEnum.overflow ? 1 : 0};
				transition-property: opacity;
				transition-duration: ${motion.duration.medium};
				transition-timing-function: ${motion.ease.easy};
				position: absolute;
				top: 0;
				left: ${(options.layout === CarouselLayoutEnum.overflow ||
            options.layout === CarouselLayoutEnum.theatre
            ? spacing.space75
            : spacing.space25) / 16}rem;
				right: ${(options.layout === CarouselLayoutEnum.overflow ||
            options.layout === CarouselLayoutEnum.theatre
            ? spacing.space75
            : spacing.space25) / 16}rem;
				pointer-events: none;
				bottom: ${(options.showScrollbar ? options.scrollBarHeight : 0) / 16}rem;
				padding-bottom: ${options.paddingBottom ? spacing.space200 / 16 : 0}rem;
				z-index: 9;
			}
			${options.isScrollbarHovered ? this.getScrollbarHovered(options.theme) : ''}
		`;
    }
    /**
     * Returns hide scrollbar style.
     *
     * @param options Options.
     * @param options.theme Theme.
     * @param options.showScrollbar Show scrollbar.
     * @returns Style.
     */
    static getFirefoxScrollbar(options) {
        if (options.showScrollbar) {
            return css `
				@-moz-document url-prefix() {
					.slideContainer {
						background: transparent;
					}
					/* Adds the illusion of a scrollbar track in Firefox */
					.slideContainer::after {
						content: '';
						position: absolute;
						bottom: ${3.5 / 16}rem;
						width: 100%;
						height: ${3 / 16}rem;
						background: ${options.theme.color.border.light};
						z-index: -1;
					}
				}
			`;
        }
        return css `
			.slideContainer {
				/* Hides scrollbar in Firefox */
				background: transparent;
			}
		`;
    }
    /**
     * Returns CSS for scrollbar hovered.
     *
     * @param theme Theme.
     * @returns Scrollbar hovered CSS.
     */
    static getScrollbarHovered(theme) {
        const { color, border } = theme;
        return css `
			.slideContainer {
				cursor: grab;
			}
			.slideContainer:active {
				cursor: grabbing;
				cursor: -moz-grabbing;
			}
			.slideContainer::-webkit-scrollbar-thumb {
				border-radius: ${border.radius.radius64 / 16}rem;
				background: linear-gradient(
					to bottom,
					transparent calc(50% - ${2 / 16}rem),
					${color.neutral.grey900} calc(50% - ${2 / 16}rem) calc(50% + ${2 / 16}rem),
					transparent calc(50% + ${2 / 16}rem)
				);
			}
			.slideContainer::-webkit-scrollbar-track {
				background: linear-gradient(
					to bottom,
					transparent calc(50% - ${2 / 16}rem),
					${color.border.light} calc(50% - ${2 / 16}rem) calc(50% + ${2 / 16}rem),
					transparent calc(50% + ${2 / 16}rem)
				);
			}
		`;
    }
    /**
     * Returns RTL styling for carousel.
     *
     * @param options ...
     * @returns Styles.
     */
    static getRTLStyling(options) {
        const { spacing } = options.theme;
        if (options.direction === 'rtl') {
            return css `
				left: auto;
				right: ${options.hasFocus ? spacing.space150 / 16 + 'rem' : `-${10000 / 16}rem`};
			`;
        }
        else {
            return css `
				left: ${options.hasFocus ? spacing.space150 / 16 + 'rem' : `-${10000 / 16}rem`};
				right: auto;
			`;
        }
    }
}
//# sourceMappingURL=CarouselStyle.js.map